'use client';

import { ReactNode, useEffect, useRef, useState } from 'react';
import { IntlProvider } from 'react-intl';

import { Locale } from '../../core/i18n/Locale';
import { TranslationMessages } from '../../core/i18n/Messages';
import { logger } from '../../core/Log/logger';
import { AmplitudeProvider } from '../../core/Tracking/Amplitude/Amplitude';
import { GlobalSetup } from '../../domains/GlobalSetup/GlobalSetup';
import { GoogleOneTap } from '../../domains/Login/Provider/Google/GoogleOneTap';
import { LoyaltyProgramModal } from '../components/LoyaltyProgramModal/LoyaltyProgramModal';
import { Marketing } from '../components/Marketing/Marketing';
import { ModalNewsLetter } from '../components/Newsletter/ModalNewsletter/ModalNewsletter';
import { SiteSuggestionModal as SiteSuggestionModalComponent } from '../components/SiteSuggestionModal/SiteSuggestionModal';
import { useSiteSuggestionModal } from '../components/SiteSuggestionModal/useSiteSuggestionModal';
import { DidomiScript } from '../utilities/GDPR/DidomiScript';
import { GTMScript } from '../utilities/GTM/GTMScript';

import { CurrencyProvider } from './currency/CurrencyProvider';
import { CurrentSiteProvider } from './currentSite/CurrentSiteProvider';
import { PageProvider } from './page/PageProvider';
import { FeatureDiscoveryNotificationProvider } from './user/FeatureDiscoveryNotificationProvider';
import { UserForProvider, UserProvider } from './user/UserProvider';

type Props = {
    locale: Locale;
    defaultLocale: Locale;
    children: ReactNode;
    seoContext: string;
    isUserConnectedOnLoad: boolean;
    intlMessages: TranslationMessages;
    gtmData: Record<string, unknown>;
    enableGoogleOneTap?: boolean;
    user: UserForProvider;
    showModalLoyaltyProgram?: boolean;
};

export function AppProviders({
    children,
    locale,
    defaultLocale,
    seoContext,
    isUserConnectedOnLoad,
    intlMessages,
    gtmData,
    enableGoogleOneTap,
    user,
    showModalLoyaltyProgram = true,
}: Props) {
    return (
        <CurrentSiteProvider locale={locale}>
            <PageProvider seoContext={seoContext}>
                <AmplitudeProvider isUserConnectedOnLoad={isUserConnectedOnLoad}>
                    <UserProvider isUserConnectedOnLoad={isUserConnectedOnLoad} user={user}>
                        <FeatureDiscoveryNotificationProvider>
                            <CurrencyProvider>
                                <IntlProvider
                                    defaultLocale={defaultLocale}
                                    locale={locale}
                                    messages={intlMessages}
                                    onError={(error) => {
                                        logger.error(
                                            'IntlProvider error',
                                            {
                                                locale,
                                                defaultLocale,
                                                seoContext,
                                            },
                                            error,
                                        );
                                    }}
                                >
                                    <GlobalSetup />
                                    <DidomiScript />
                                    <GTMScript gtmData={gtmData} />
                                    <div id="modal-wrapper" />
                                    <ModalNewsLetter />
                                    <SiteSuggestionModal />
                                    <Marketing />
                                    {showModalLoyaltyProgram ? (
                                        <LoyaltyProgramModal seoContext={seoContext} />
                                    ) : null}
                                    {enableGoogleOneTap ? <GoogleOneTap /> : null}
                                    {children}
                                </IntlProvider>
                            </CurrencyProvider>
                        </FeatureDiscoveryNotificationProvider>
                    </UserProvider>
                </AmplitudeProvider>
            </PageProvider>
        </CurrentSiteProvider>
    );
}

function SiteSuggestionModal() {
    const siteSuggestion = useSiteSuggestionModal();
    const [shouldShowModal, setShouldShowModal] = useState(false);
    const hasBeenSetRef = useRef(false);

    useEffect(() => {
        if (siteSuggestion && !hasBeenSetRef.current) {
            hasBeenSetRef.current = true;
            setShouldShowModal(true);
        }
    }, [siteSuggestion]);

    return shouldShowModal ? <SiteSuggestionModalComponent /> : null;
}
